import { useState } from 'react'
import { motion } from 'framer-motion'
import type { Job, RuntimeTaskStatus } from '@workflow/contracts'
import { RUNTIME_TASK_KINDS, type DispatchTaskInput, type RuntimeTaskDetail } from '@workflow/contracts'
import { Button, ConfirmButton, Dropdown, EmptyNote, Field, FADE, Loading, StateNote, StatusLabel, TextField } from '@workflow/ui'
import type { StatusTone } from '@workflow/ui'
import { describeError } from '@/lib/api/client'
import { useCancelJobTask, useJobDispatch, useRuntimes, useDispatchJobTask } from '@/lib/api/hooks'
import { formatRelative, formatTimestamp } from '@/lib/format'

/**
 * A job's dispatch list: the tasks the studio has put on the machine queue.
 * A task leaves here as words and payload, comes back as a status and an
 * event log; the queue between — retries, reclaims, the attempts cap — is
 * the runtime module's promise, and each row shows where its work stands.
 */

const TASK_STATUS: Record<RuntimeTaskStatus, { label: string; tone: StatusTone }> = {
  queued: { label: 'Queued', tone: 'waiting' },
  claimed: { label: 'With a runtime', tone: 'active' },
  succeeded: { label: 'Done', tone: 'done' },
  failed: { label: 'Failed', tone: 'attention' },
  cancelled: { label: 'Called back', tone: 'idle' },
}

const KIND_LABEL: Record<string, string> = {
  delivery: 'Delivery',
}

export function JobDispatch({ jobId, job }: { jobId: string; job: Job }) {
  const dispatch = useJobDispatch(jobId)
  const [adding, setAdding] = useState(false)
  const open = !job.archivedAt && (job.status === 'published' || job.status === 'in_progress')

  return (
    <div className="flex flex-col gap-3">
      {adding ? (
        <DispatchForm jobId={jobId} onDone={() => setAdding(false)} />
      ) : open ? (
        <div>
          <Button size="sm" onClick={() => setAdding(true)}>
            Dispatch a task
          </Button>
        </div>
      ) : null}

      {dispatch.isLoading ? (
        <Loading label="Loading the queue" />
      ) : dispatch.error ? (
        <StateNote title="The dispatch list did not load." detail={describeError(dispatch.error)} />
      ) : (dispatch.data?.tasks.length ?? 0) === 0 ? (
        <EmptyNote
          title="Nothing on the machine queue."
          detail={
            open
              ? 'Dispatch a task when this job needs a machine — a delivery, an automation.'
              : 'Publish or resume the job before dispatching work.'
          }
        />
      ) : (
        <ul className="flex flex-col">
          {dispatch.data!.tasks.map((task) => (
            <TaskRow key={task.id} jobId={jobId} task={task} />
          ))}
        </ul>
      )}
    </div>
  )
}

function DispatchForm({ jobId, onDone }: { jobId: string; onDone: () => void }) {
  const [kind, setKind] = useState<string>(RUNTIME_TASK_KINDS[0]!)
  const [runtimeId, setRuntimeId] = useState('')
  const [payload, setPayload] = useState('')
  const [payloadFault, setPayloadFault] = useState<string | null>(null)
  const runtimes = useRuntimes()
  const dispatch = useDispatchJobTask()

  const activeRuntimes = (runtimes.data ?? []).filter((runtime) => runtime.status === 'active')

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    setPayloadFault(null)
    let parsedPayload: Record<string, unknown> | undefined
    if (payload.trim()) {
      try {
        const parsed = JSON.parse(payload) as unknown
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          setPayloadFault('The payload must be a JSON object.')
          return
        }
        parsedPayload = parsed as Record<string, unknown>
      } catch {
        setPayloadFault('The payload is not valid JSON.')
        return
      }
    }
    const input: DispatchTaskInput = {
      kind: kind as DispatchTaskInput['kind'],
      ...(parsedPayload ? { payload: parsedPayload } : {}),
      ...(runtimeId ? { runtimeId } : {}),
    }
    dispatch.mutate({ jobId, input }, { onSuccess: onDone })
  }

  return (
    <motion.form {...FADE} onSubmit={submit} className="rounded-xl border bg-surface px-4 py-3 shadow-chip">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Kind" htmlFor="dispatch-kind" className="min-w-40 flex-1">
          <Dropdown id="dispatch-kind" ariaLabel="Kind" value={kind} onChange={setKind}
            options={RUNTIME_TASK_KINDS.map((entry) => ({ value: entry, label: KIND_LABEL[entry] ?? entry }))} />
        </Field>
        <Field label="Runtime" htmlFor="dispatch-runtime" hint="Leave open for any machine that can run it." className="min-w-48 flex-1">
          <Dropdown id="dispatch-runtime" ariaLabel="Runtime" value={runtimeId || 'any'}
            onChange={(value) => setRuntimeId(value === 'any' ? '' : value)}
            options={[{ value: 'any', label: 'Any runtime' }, ...activeRuntimes.map((runtime) => ({ value: runtime.id, label: runtime.name }))]} />
        </Field>
        <Field label="Payload" htmlFor="dispatch-payload" hint="What the task carries — a JSON object, optional.">
          <TextField
            id="dispatch-payload"
            value={payload}
            placeholder='e.g. { "build": "1.4.2" }'
            onChange={(event) => setPayload(event.target.value)}
          />
        </Field>
        <Button type="submit" size="md" variant="primary" busy={dispatch.isPending}>
          Dispatch
        </Button>
        <Button type="button" size="md" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
      {payloadFault ? <p className="mt-2 text-caption text-danger">{payloadFault}</p> : null}
      {dispatch.error ? <p className="mt-2 text-caption text-danger">{describeError(dispatch.error)}</p> : null}
    </motion.form>
  )
}

function TaskRow({ jobId, task }: { jobId: string; task: RuntimeTaskDetail }) {
  const cancel = useCancelJobTask()
  const shown = TASK_STATUS[task.status]
  const waiting = task.nextAttemptAt !== null && task.status === 'queued'

  return (
    <li className="border-b border-hairline py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-ui text-foreground">
            {KIND_LABEL[task.kind] ?? task.kind}
            <span className="ml-2 font-mono text-caption text-muted-foreground">{task.id}</span>
          </span>
          <span className="block text-caption text-muted-foreground">
            {[
              task.runtimeId === null ? 'waiting for any runtime' : `held by ${task.runtimeName ?? 'a runtime'}`,
              task.attempts > 0 ? `tried ${task.attempts} ${task.attempts === 1 ? 'time' : 'times'}` : null,
              waiting ? `next try ${formatRelative(task.nextAttemptAt!)}` : null,
              `dispatched by ${task.createdByName ?? task.createdBy}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {task.lastError ? <span className="block truncate text-caption text-danger">{task.lastError}</span> : null}
        </span>
        <span className="text-caption text-muted-foreground" title="Dispatched">
          {formatRelative(task.createdAt)}
        </span>
        <StatusLabel tone={shown.tone} label={shown.label} />
        {task.status === 'queued' ? (
          <ConfirmButton
            label="Call back"
            confirmLabel="Really call back?"
            busy={cancel.isPending}
            onConfirm={() => cancel.mutate({ jobId, taskId: task.id })}
          />
        ) : null}
      </div>
      {/* The task's own event log — the machine's story, one line, oldest first. */}
      {task.events.length > 0 ? (
        <p className="mt-1 text-caption text-muted-foreground">
          {task.events
            .map((event) => `${event.event} ${formatRelative(event.at)}`)
            .join(' → ')}
          <span className="sr-only"> ({formatTimestamp(task.events[0]!.at)} onward)</span>
        </p>
      ) : null}
      {cancel.error ? <p className="mt-1 text-caption text-danger">{describeError(cancel.error)}</p> : null}
    </li>
  )
}
